import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { ArrowRight, ChevronDown, Clock3, Compass, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { cn } from "../../lib/utils";

/**
 * Intent cards: "what do you want to do?" entry points for screens that are
 * heavy for a first-time planner (onboarding, Översikt, Rapporter).
 *
 * The panel asks one question and offers two to four answers. Each answer is a
 * card whose icon stands on stacked plates; on hover the card tilts toward the
 * pointer, the plates fan apart and a tone-tinted spotlight follows the cursor.
 * Motion is skipped entirely under `prefers-reduced-motion`.
 *
 * Dismissal is controlled by the consumer (this package reads no storage): pass
 * `onDismiss` to show the close button, and render `IntentPanelReopen` in the
 * panel's place once dismissed so the guidance is always recoverable.
 *
 * Design-system contract: wiki/guides/ux/design-system.md ("Intent cards").
 */

/**
 * Brand 2.2 domain tones say what the answer is about (planning, care,
 * continuity, attention, human, society). The older brand tones stay for
 * panels that have not moved yet.
 */
export type IntentDomainTone =
  | "planning"
  | "care"
  | "continuity"
  | "attention"
  | "human"
  | "society";

export type IntentTone = "coral" | "ink" | "pos" | "aurora" | IntentDomainTone;

/** Tone → plate, icon and spotlight colours. Brand tokens only, no hex. */
const TONE: Record<
  IntentTone,
  { plate: string; plateBack: string; icon: string; spot: string; edge: string }
> = {
  coral: {
    plate: "bg-brand-coral-bg",
    plateBack: "bg-[hsl(var(--brand-coral)/0.25)]",
    icon: "text-brand-coral-text",
    spot: "hsl(var(--brand-coral) / 0.16)",
    edge: "group-hover/intent:border-[hsl(var(--brand-coral)/0.45)]",
  },
  ink: {
    plate: "bg-brand-sand",
    plateBack: "bg-[hsl(var(--brand-ink)/0.15)]",
    icon: "text-brand-ink",
    spot: "hsl(var(--brand-ink) / 0.08)",
    edge: "group-hover/intent:border-[hsl(var(--brand-ink)/0.3)]",
  },
  pos: {
    plate: "bg-brand-pos-bg",
    plateBack: "bg-[hsl(var(--brand-pos)/0.25)]",
    icon: "text-brand-pos",
    spot: "hsl(var(--brand-pos) / 0.14)",
    edge: "group-hover/intent:border-[hsl(var(--brand-pos)/0.4)]",
  },
  // Aurora marks AI provenance only (design-system colour role 2).
  aurora: {
    plate:
      "bg-[linear-gradient(120deg,hsl(var(--brand-aurora-cyan)/0.14),hsl(var(--brand-aurora-blue)/0.14),hsl(var(--brand-aurora-magenta)/0.14))]",
    plateBack: "bg-[image:var(--gradient-ai)] opacity-30",
    icon: "text-[hsl(var(--brand-aurora-blue))]",
    spot: "hsl(var(--brand-aurora-blue) / 0.12)",
    edge: "group-hover/intent:border-[hsl(var(--brand-aurora-blue)/0.4)]",
  },
  // Brand 2.2: plate = domain tint, icon = domain ink. Literal strings so the
  // JIT sees them.
  planning: {
    plate: "bg-domain-planning-tint",
    plateBack: "bg-domain-planning-solid/25",
    icon: "text-domain-planning-ink",
    spot: "hsl(var(--cb-domain-planning-solid-hsl) / 0.12)",
    edge: "group-hover/intent:border-domain-planning-solid/40",
  },
  care: {
    plate: "bg-domain-care-tint",
    plateBack: "bg-domain-care-solid/25",
    icon: "text-domain-care-ink",
    spot: "hsl(var(--cb-domain-care-solid-hsl) / 0.12)",
    edge: "group-hover/intent:border-domain-care-solid/40",
  },
  continuity: {
    plate: "bg-domain-continuity-tint",
    plateBack: "bg-domain-continuity-solid/25",
    icon: "text-domain-continuity-ink",
    spot: "hsl(var(--cb-domain-continuity-solid-hsl) / 0.12)",
    edge: "group-hover/intent:border-domain-continuity-solid/40",
  },
  attention: {
    plate: "bg-domain-attention-tint",
    plateBack: "bg-domain-attention-solid/25",
    icon: "text-domain-attention-ink",
    spot: "hsl(var(--cb-domain-attention-solid-hsl) / 0.12)",
    edge: "group-hover/intent:border-domain-attention-solid/40",
  },
  human: {
    plate: "bg-domain-human-tint",
    plateBack: "bg-domain-human-solid/25",
    icon: "text-domain-human-ink",
    spot: "hsl(var(--cb-domain-human-solid-hsl) / 0.12)",
    edge: "group-hover/intent:border-domain-human-solid/40",
  },
  society: {
    plate: "bg-domain-society-tint",
    plateBack: "bg-domain-society-solid/25",
    icon: "text-domain-society-ink",
    spot: "hsl(var(--cb-domain-society-solid-hsl) / 0.12)",
    edge: "group-hover/intent:border-domain-society-solid/40",
  },
};

/** The recommended card's fill: the human tint (Brand 2.2 "recommended"). */
const RECOMMENDED_FILL = "border-domain-human-solid/30 bg-domain-human-tint";

const MAX_TILT_DEG = 4;

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/* -------------------------------------------------------------------------- */
/* Panel                                                                      */
/* -------------------------------------------------------------------------- */

export interface IntentPanelProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  "title"
> {
  /** Short label above the question, e.g. "Kom igång". */
  eyebrow?: string;
  /** The question the cards answer — "Vad vill ni göra?". */
  title: string;
  description?: string;
  /** Shows the close button. The consumer persists the dismissal. */
  onDismiss?: () => void;
  /** Accessible name of the close button. Required with `onDismiss`. */
  dismissLabel?: string;
  /** Short line under the close button's tooltip, e.g. "Visas inte igen". */
  dismissHint?: string;
}

export const IntentPanel = React.forwardRef<HTMLElement, IntentPanelProps>(
  (
    {
      eyebrow,
      title,
      description,
      onDismiss,
      dismissLabel = "Dölj",
      dismissHint,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const titleId = React.useId();
    return (
      <section
        ref={ref}
        aria-labelledby={titleId}
        className={cn(
          "relative overflow-hidden rounded-xl border border-border p-5 shadow-soft sm:p-6",
          // A warm wash: cream → sand, with a faint coral bloom top-right.
          "bg-[radial-gradient(120%_90%_at_100%_0%,hsl(var(--brand-coral)/0.10),transparent_55%),linear-gradient(180deg,hsl(var(--brand-cream)),hsl(var(--brand-sand)/0.7))]",
          className,
        )}
        {...props}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-1">
            {eyebrow ? (
              <p className="flex items-center gap-1.5 font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-brand-coral-text">
                <Compass aria-hidden="true" className="h-3.5 w-3.5" />
                {eyebrow}
              </p>
            ) : null}
            <h2
              id={titleId}
              className="text-xl font-bold tracking-tight text-brand-ink"
            >
              {title}
            </h2>
            {description ? (
              <p className="max-w-[46rem] text-sm leading-6 text-muted-foreground">
                {description}
              </p>
            ) : null}
          </div>
          {onDismiss ? (
            <button
              type="button"
              onClick={onDismiss}
              aria-label={dismissLabel}
              title={
                dismissHint ? `${dismissLabel} — ${dismissHint}` : dismissLabel
              }
              data-testid="intent-panel-dismiss"
              className="-mr-1 -mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[hsl(var(--brand-ink)/0.05)] hover:text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </button>
          ) : null}
        </div>
        {children}
      </section>
    );
  },
);
IntentPanel.displayName = "IntentPanel";

/** Responsive grid for two to four cards. */
export function IntentCardGrid({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "grid gap-4 [perspective:1200px] sm:grid-cols-2 xl:grid-cols-[repeat(auto-fit,minmax(15rem,1fr))]",
        className,
      )}
      {...props}
    />
  );
}

/**
 * Shown where a dismissed panel used to be, so a planner can always bring the
 * guidance back. Quiet by design: one line, no card.
 */
export function IntentPanelReopen({
  label,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      data-testid="intent-panel-reopen"
      className={cn(
        "inline-flex items-center gap-1.5 self-start rounded-full border border-dashed border-border px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-[hsl(var(--brand-coral)/0.5)] hover:bg-brand-coral-bg hover:text-brand-coral-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
      {...props}
    >
      <Compass aria-hidden="true" className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Card                                                                       */
/* -------------------------------------------------------------------------- */

export interface IntentCardProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "title"
> {
  icon: LucideIcon;
  title: string;
  description: string;
  tone?: IntentTone;
  /** Time or effort hint, e.g. "≈ 5 min". Rendered in mono. */
  estimate?: string;
  /** Small pill, e.g. "Rekommenderat". */
  badge?: string;
  /**
   * The one recommended answer in a panel: human-tint fill. At most one card
   * per panel; pair it with a `badge` that says why.
   */
  recommended?: boolean;
  /** Collapsible detail behind a "Läs mer" toggle. */
  readMore?: React.ReactNode;
  readMoreLabel?: string;
  readLessLabel?: string;
  /** Exactly one `IntentCardAction` — it becomes the whole card's hit area. */
  children: React.ReactNode;
}

export const IntentCard = React.forwardRef<HTMLDivElement, IntentCardProps>(
  (
    {
      icon: Icon,
      title,
      description,
      tone = "coral",
      estimate,
      badge,
      recommended = false,
      readMore,
      readMoreLabel = "Läs mer",
      readLessLabel = "Visa mindre",
      className,
      children,
      style,
      ...props
    },
    ref,
  ) => {
    const toneClass = TONE[tone];
    const [expanded, setExpanded] = React.useState(false);
    const detailId = React.useId();

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
      const el = event.currentTarget;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;
      el.style.setProperty("--intent-mx", `${(x * 100).toFixed(1)}%`);
      el.style.setProperty("--intent-my", `${(y * 100).toFixed(1)}%`);
      if (prefersReducedMotion()) return;
      el.style.setProperty(
        "--intent-rx",
        `${((0.5 - y) * MAX_TILT_DEG).toFixed(2)}deg`,
      );
      el.style.setProperty(
        "--intent-ry",
        `${((x - 0.5) * MAX_TILT_DEG).toFixed(2)}deg`,
      );
    };
    const handlePointerLeave = (event: React.PointerEvent<HTMLDivElement>) => {
      const el = event.currentTarget;
      el.style.setProperty("--intent-rx", "0deg");
      el.style.setProperty("--intent-ry", "0deg");
    };

    return (
      <div
        ref={ref}
        data-tone={tone}
        data-recommended={recommended || undefined}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
        style={
          {
            "--intent-spot": toneClass.spot,
            ...style,
          } as React.CSSProperties
        }
        className={cn(
          "group/intent relative flex h-full flex-col rounded-xl border border-border bg-card p-5 shadow-soft",
          // 3D: tilt toward the pointer and lift. Transform only, no layout.
          "[transform-style:preserve-3d] [transform:perspective(900px)_translateY(var(--intent-lift,0px))_rotateX(var(--intent-rx,0deg))_rotateY(var(--intent-ry,0deg))]",
          "transition-[transform,box-shadow,border-color] duration-200 ease-out",
          "hover:[--intent-lift:-3px] hover:shadow-[0_18px_40px_-18px_hsl(var(--brand-ink)/0.28),0_2px_6px_hsl(var(--brand-ink)/0.06)]",
          "focus-within:shadow-medium focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background",
          "motion-reduce:transition-none motion-reduce:[transform:none]",
          toneClass.edge,
          recommended && RECOMMENDED_FILL,
          className,
        )}
        {...props}
      >
        {/* Spotlight that follows the pointer. Decorative and click-through;
            it may tint the unpositioned footer faintly on hover. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-300 [background:radial-gradient(28rem_circle_at_var(--intent-mx,50%)_var(--intent-my,0%),var(--intent-spot),transparent_60%)] group-hover/intent:opacity-100"
        />

        <div className="relative mb-4 flex items-start justify-between gap-3">
          <IntentPlates icon={Icon} tone={tone} />
          {badge ? (
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                recommended
                  ? "bg-domain-human-ink text-caire-on-solid"
                  : "border border-[hsl(var(--brand-coral)/0.3)] bg-brand-coral-bg text-brand-coral-text",
              )}
            >
              {badge}
            </span>
          ) : null}
        </div>

        <h3 className="relative text-base font-semibold leading-snug text-brand-ink">
          {title}
        </h3>
        <p className="relative mt-1.5 text-sm leading-6 text-muted-foreground">
          {description}
        </p>

        {readMore ? (
          <div className="relative z-10 mt-2">
            <button
              type="button"
              aria-expanded={expanded}
              aria-controls={detailId}
              onClick={() => setExpanded((open) => !open)}
              className="inline-flex items-center gap-1 rounded text-xs font-semibold text-brand-coral-text underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {expanded ? readLessLabel : readMoreLabel}
              <ChevronDown
                aria-hidden="true"
                className={cn(
                  "h-3.5 w-3.5 transition-transform motion-reduce:transition-none",
                  expanded && "rotate-180",
                )}
              />
            </button>
            <div
              id={detailId}
              hidden={!expanded}
              className="mt-2 rounded-lg border border-border bg-[hsl(var(--brand-sand)/0.6)] p-3 text-xs leading-5 text-[hsl(var(--brand-ink)/0.8)]"
            >
              {readMore}
            </div>
          </div>
        ) : null}

        {/* Not positioned: the action's ::after must anchor to the card so
            the whole card is the hit area. */}
        <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-5">
          {estimate ? (
            <span className="inline-flex items-center gap-1.5 font-mono text-[11.5px] tabular-nums text-muted-foreground">
              <Clock3 aria-hidden="true" className="h-3.5 w-3.5" />
              {estimate}
            </span>
          ) : (
            <span />
          )}
          {children}
        </div>
      </div>
    );
  },
);
IntentCard.displayName = "IntentCard";

/**
 * The card's one action. Its `::after` stretches over the whole card so the
 * card is clickable, while `readMore` (z-10) stays independently operable.
 * Use `asChild` to wrap a router link.
 */
export const IntentCardAction = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }
>(({ asChild, className, children, ...props }, ref) => {
  const className_ = cn(
    "inline-flex items-center gap-1.5 rounded-md text-sm font-semibold text-brand-coral-text",
    "after:absolute after:inset-0 after:rounded-xl after:content-['']",
    "focus-visible:outline-none",
    className,
  );
  if (
    asChild &&
    React.isValidElement<{ children?: React.ReactNode }>(children)
  ) {
    // Append the arrow inside the child itself, so Slot merges the classes
    // (and the stretched hit area) onto the link rather than onto a wrapper.
    return (
      <Slot ref={ref} className={className_} {...props}>
        {React.cloneElement(
          children,
          undefined,
          <>
            {children.props.children}
            <IntentArrow />
          </>,
        )}
      </Slot>
    );
  }
  return (
    <button ref={ref} type="button" className={className_} {...props}>
      {children}
      <IntentArrow />
    </button>
  );
});
IntentCardAction.displayName = "IntentCardAction";

function IntentArrow() {
  return (
    <ArrowRight
      aria-hidden="true"
      className="h-4 w-4 transition-transform duration-200 group-hover/intent:translate-x-1 motion-reduce:transition-none"
    />
  );
}

/**
 * The signature: the icon on three stacked plates. At rest they sit almost
 * flush; on hover they fan out along Z so the card reads as an object.
 */
function IntentPlates({
  icon: Icon,
  tone,
}: {
  icon: LucideIcon;
  tone: IntentTone;
}) {
  const t = TONE[tone];
  const plate =
    "absolute inset-0 rounded-[10px] transition-transform duration-300 ease-out motion-reduce:transition-none";
  return (
    <span
      aria-hidden="true"
      className="relative h-12 w-12 shrink-0 [transform-style:preserve-3d]"
    >
      <span
        className={cn(
          plate,
          t.plateBack,
          "[transform:translate3d(4px,4px,0)] group-hover/intent:[transform:translate3d(9px,9px,-12px)]",
        )}
      />
      <span
        className={cn(
          plate,
          t.plateBack,
          "opacity-60 [transform:translate3d(2px,2px,0)] group-hover/intent:[transform:translate3d(5px,5px,-6px)]",
        )}
      />
      <span
        className={cn(
          plate,
          t.plate,
          "flex items-center justify-center border border-white/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)] group-hover/intent:[transform:translate3d(-2px,-2px,10px)]",
        )}
      >
        <Icon className={cn("h-5 w-5", t.icon)} strokeWidth={1.75} />
      </span>
    </span>
  );
}
